import React from 'react'
import { IonRow, IonCol, IonList, IonLabel } from '@ionic/react'
import SingleSelect from '../../../../../components/singleSelect/SingleSelect.component'
import { getObstetricsSelector } from '../selectors'
import { useDispatch, useSelector } from 'react-redux'
import { IOption } from '../../../../../common/common.types'
import {
  setBloodyShow,
  setContractionsDur,
  setContractionsFreq,
  setDilation,
  setEffacement,
  setExpectedDateOfConfinement,
  setGestationAgeDays,
  setGestationAgeWeeks,
  setGravida,
  setInitialFetalHeartRate,
  setIsPatientPregnant,
  setIsPatientPregnantPNNV,
  setLastMenstrualPeriod,
  setMaternalMedicationsAdministered,
  setMembranes,
  setPara,
  setPrenatalCare,
  setPresentingPart,
  setRuptureTime,
  setComments,
  saveObstetrics,
} from '../store/obstetrics.actions'
import { useTranslation } from 'react-i18next'
import { getObstetricsptionsSelector } from '../../../../options/selectors/getObstetricsOptions.selector'
import MultiSelect from '../../../../../components/multiSelect/MultiSelect.component'
import NumberPad from '../../../../../components/numberPad/NumberPad.component'
import SInput from '../../../../../components/stringInput/StringInput.component'
import Calendar from '../../../../../components/calendar/Calendar.component'
import { hyphenDotKeys } from '../../../../../components/numberPad/NumberPad.types'
import Validation from '../../../../../components/validation/Validation.component'
import { ObstetricsConstants } from '../store/obstetrics.constants'

const ObstetricsContent: React.FC<any> = () => {
  const obstetricsState = useSelector(getObstetricsSelector)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const obstetricsOptions = useSelector(getObstetricsptionsSelector)

  const {
    isPatientPregnantOptions,
    bloodyShowOptions,
    gestationAgeWeeksOptions,
    gestationAgeDaysOptions,
    effacementOptions,
    prenatalCareOptions,
    membranesOptions,
    maternalMedicationsAdministeredOptions,
  } = obstetricsOptions

  const {
    isPatientPregnant,
    isPatientPregnantPNNV,
    gravida,
    para,
    gestationAgeWeeks,
    gestationAgeDays,
    expectedDateOfConfinement,
    lastMenstrualPeriod,
    initialFetalHeartRate,
    contractionsDur,
    contractionsFreq,
    membranes,
    ruptureTime,
    dilation,
    effacement,
    maternalMedicationsAdministered,
    presentingPart,
    bloodyShow,
    prenatalCare,
  } = obstetricsState

  const hasPNValues = (isPatientPregnantPNNV && [8801019, 8801023].includes(isPatientPregnantPNNV.id)) || false

  const save = () => {
    dispatch(saveObstetrics())
  }

  const setIsPatientPregnantOnChange = (option: IOption) => {
    dispatch(setIsPatientPregnant(option))
    save()
  }

  const setIsPatientPregnantPNNVOnChange = (option: IOption) => {
    dispatch(setIsPatientPregnantPNNV(option))
    if (option?.id === 8801019 || option?.id === 8801023) {
      dispatch(setGravida(''))
      dispatch(setPara(''))
      dispatch(setGestationAgeWeeks(null))
      dispatch(setExpectedDateOfConfinement(''))
      dispatch(setLastMenstrualPeriod(''))
      dispatch(setInitialFetalHeartRate(''))
      dispatch(setContractionsDur(''))
      dispatch(setContractionsFreq(''))
      dispatch(setRuptureTime(''))
      dispatch(setDilation(''))
      dispatch(setEffacement(null))
      dispatch(setPresentingPart(''))
      dispatch(setBloodyShow(null))
      dispatch(setPrenatalCare(null))
      dispatch(setComments(''))
    }
    save()
  }

  const setGestationAgeWeeksOnChange = (option: IOption) => {
    dispatch(setGestationAgeWeeks(option))
    save()
  }

  const setGestationAgeDaysOnChange = (option: IOption) => {
    dispatch(setGestationAgeDays(option))
    save()
  }

  const setExpectedDateOfConfinementOnChange = (value: string | null) => {
    dispatch(setExpectedDateOfConfinement(value))
    save()
  }

  const setLastMenstrualPeriodOnChange = (value: string | null) => {
    dispatch(setLastMenstrualPeriod(value))
    save()
  }

  const setInitialFetalHeartRateOnChange = (numbers: string[]) => {
    let newInitialFetalHeartRate = ''
    if (numbers.length) {
      newInitialFetalHeartRate = numbers.join('')
    }
    dispatch(setInitialFetalHeartRate(newInitialFetalHeartRate))
    save()
  }

  const setContractionsFreqOnChange = (numbers: string[]) => {
    let newContractionsFreq = ''
    if (numbers.length) {
      newContractionsFreq = numbers.join('')
    }
    dispatch(setContractionsFreq(newContractionsFreq))
    save()
  }

  const setContractionsDurOnChange = (numbers: string[]) => {
    let newContractionsDur = ''
    if (numbers.length) {
      newContractionsDur = numbers.join('')
    }
    dispatch(setContractionsDur(newContractionsDur))
    save()
  }

  const setRuptureTimeOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setRuptureTime(e.detail.value))
    save()
  }

  const setGravidaOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setGravida(e.detail.value))
    save()
  }

  const setParaOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setPara(e.detail.value))
    save()
  }

  const setPresentingPartOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setPresentingPart(e.detail.value))
    save()
  }

  const setDialationOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setDilation(e.detail.value))
    save()
  }

  const setEffacementOnChange = (option: IOption) => {
    dispatch(setEffacement(option))
    save()
  }

  const setBloodyShowOnChange = (option: IOption) => {
    dispatch(setBloodyShow(option))
    save()
  }

  const setPrenatalCareOnChange = (option: IOption) => {
    dispatch(setPrenatalCare(option))
    save()
  }

  return (
    <div id='ObstetricsContent'>
      <IonList>
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID={t('additionalAssessments.obstetrics.isPatientPregnant')}
              options={isPatientPregnantOptions}
              selectedOption={isPatientPregnant}
              listHeader={t('additionalAssessments.obstetrics.isPatientPregnant')}
              label={t('additionalAssessments.obstetrics.isPatientPregnant')}
              action={setIsPatientPregnantOnChange}
              selectedPNNVOption={isPatientPregnantPNNV}
              pnnvAction={setIsPatientPregnantPNNVOnChange}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='heading-text'>
            <IonLabel>{t('additionalAssessments.obstetrics.maternalHistory')}</IonLabel>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol size='6' className='picklist'>
            <SInput
              label={t('additionalAssessments.obstetrics.gravida')}
              dataTestID={t('additionalAssessments.obstetrics.gravida')}
              inputValue={gravida}
              onChangeAction={setGravidaOnChange}
              disabled={hasPNValues}
            />
            <Validation id={ObstetricsConstants.SET_GRAVIDA} />
          </IonCol>

          <IonCol size='6' className='picklist'>
            <SInput
              label={t('additionalAssessments.obstetrics.para')}
              dataTestID={t('additionalAssessments.obstetrics.para')}
              inputValue={para}
              onChangeAction={setParaOnChange}
              disabled={hasPNValues}
            />
            <Validation id={ObstetricsConstants.SET_PARA} />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='heading-text'>
            <IonLabel>{t('additionalAssessments.obstetrics.gestationalAge')}</IonLabel>
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol size='6' className='picklist'>
            <SingleSelect
              dataTestID={t('additionalAssessments.obstetrics.gestationAgeWeeks')}
              options={gestationAgeWeeksOptions}
              selectedOption={gestationAgeWeeks}
              listHeader={t('additionalAssessments.obstetrics.gestationAgeWeeks')}
              label={t('additionalAssessments.obstetrics.gestationAgeWeeks')}
              action={setGestationAgeWeeksOnChange}
              disabled={hasPNValues}
            />
          </IonCol>
          <IonCol size='6' className='picklist'>
            <SingleSelect
              dataTestID={t('additionalAssessments.obstetrics.gestationAgeDays')}
              options={gestationAgeDaysOptions}
              selectedOption={gestationAgeDays}
              listHeader={t('additionalAssessments.obstetrics.gestationAgeDays')}
              label={t('additionalAssessments.obstetrics.gestationAgeDays')}
              action={setGestationAgeDaysOnChange}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <Calendar
            showTimeSection={false}
            dataTestID={t('additionalAssessments.obstetrics.expectedDateOfConfinement')}
            selectedDate={expectedDateOfConfinement}
            action={setExpectedDateOfConfinementOnChange}
            toggleAction={() => ''}
            label={t('additionalAssessments.obstetrics.expectedDateOfConfinement')}
            disabled={hasPNValues}
          />
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <Calendar
            showTimeSection={false}
            dataTestID={t('additionalAssessments.obstetrics.lastMenstrualPeriod')}
            selectedDate={lastMenstrualPeriod}
            action={setLastMenstrualPeriodOnChange}
            toggleAction={() => ''}
            label={t('additionalAssessments.obstetrics.lastMenstrualPeriod')}
            disabled={hasPNValues}
          />
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <NumberPad
              elementId={t('additionalAssessments.obstetrics.initialFetalHeartRate')}
              dataTestId={t('additionalAssessments.obstetrics.initialFetalHeartRate')}
              values={initialFetalHeartRate ? initialFetalHeartRate.toString().split('') : []}
              label={t('additionalAssessments.obstetrics.initialFetalHeartRate')}
              onNumPadClick={setInitialFetalHeartRateOnChange}
              validCharacters={hyphenDotKeys}
              disabled={hasPNValues}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='heading-text'>
            <IonLabel>{t('additionalAssessments.obstetrics.contractions')}</IonLabel>
          </IonCol>
        </IonRow>
        <IonRow id={t('additionalAssessments.obstetrics.contractions')}>
          <IonCol size='6' className='picklist'>
            <NumberPad
              dataTestId={t('additionalAssessments.obstetrics.contractionsFreq')}
              values={contractionsFreq ? contractionsFreq.toString().split('') : []}
              label={t('additionalAssessments.obstetrics.contractionsFreq')}
              onNumPadClick={setContractionsFreqOnChange}
              validCharacters={hyphenDotKeys}
              disabled={hasPNValues}
            />
          </IonCol>
          <IonCol size='6' className='picklist'>
            <NumberPad
              dataTestId={t('additionalAssessments.obstetrics.contractionsDur')}
              values={contractionsDur ? contractionsDur.toString().split('') : []}
              label={t('additionalAssessments.obstetrics.contractionsDur')}
              onNumPadClick={setContractionsDurOnChange}
              validCharacters={hyphenDotKeys}
              disabled={hasPNValues}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist ion-no-margin'>
            <MultiSelect
              showChips={true}
              dataTestID={t('additionalAssessments.obstetrics.membranes')}
              listHeader={t('additionalAssessments.obstetrics.membranes')}
              options={membranesOptions}
              savedValues={membranes}
              action={setMembranes}
              label={t('additionalAssessments.obstetrics.membranes')}
              callback={saveObstetrics}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <SInput
            label={t('additionalAssessments.obstetrics.ruptureTime')}
            dataTestID={t('additionalAssessments.obstetrics.ruptureTime')}
            inputValue={ruptureTime}
            onChangeAction={setRuptureTimeOnChange}
            disabled={hasPNValues}
          />
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol size='6' className='picklist'>
            <SInput
              label={t('additionalAssessments.obstetrics.dilation')}
              dataTestID={t('additionalAssessments.obstetrics.dilation')}
              inputValue={dilation}
              onChangeAction={setDialationOnChange}
              disabled={hasPNValues}
            />
            <Validation id={ObstetricsConstants.SET_DILATION} />
          </IonCol>

          <IonCol size='6' className='picklist'>
            <SingleSelect
              dataTestID={t('additionalAssessments.obstetrics.effacement')}
              options={effacementOptions}
              selectedOption={effacement}
              listHeader={t('additionalAssessments.obstetrics.effacement')}
              label={t('additionalAssessments.obstetrics.effacement')}
              action={setEffacementOnChange}
              disabled={hasPNValues}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist ion-no-margin'>
            <MultiSelect
              showChips={true}
              dataTestID={t('additionalAssessments.obstetrics.maternalMedicationsAdministered')}
              listHeader={t('additionalAssessments.obstetrics.maternalMedicationsAdministered')}
              options={maternalMedicationsAdministeredOptions}
              savedValues={maternalMedicationsAdministered}
              action={setMaternalMedicationsAdministered}
              label={t('additionalAssessments.obstetrics.maternalMedicationsAdministered')}
              callback={saveObstetrics}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <SInput
              label={t('additionalAssessments.obstetrics.presentingPart')}
              dataTestID={t('additionalAssessments.obstetrics.presentingPart')}
              inputValue={presentingPart}
              onChangeAction={setPresentingPartOnChange}
              disabled={hasPNValues}
            />
            <Validation id={ObstetricsConstants.SET_PRESENTING_PART} />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID={t('additionalAssessments.obstetrics.bloodyShow')}
              options={bloodyShowOptions}
              selectedOption={bloodyShow}
              listHeader={t('additionalAssessments.obstetrics.bloodyShow')}
              label={t('additionalAssessments.obstetrics.bloodyShow')}
              action={setBloodyShowOnChange}
              disabled={hasPNValues}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID={t('additionalAssessments.obstetrics.prenatalCare')}
              options={prenatalCareOptions}
              selectedOption={prenatalCare}
              listHeader={t('additionalAssessments.obstetrics.prenatalCare')}
              label={t('additionalAssessments.obstetrics.prenatalCare')}
              action={setPrenatalCareOnChange}
              disabled={hasPNValues}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
      </IonList>
    </div>
  )
}

export default ObstetricsContent
